import { useEffect } from "react";
import Select from "react-select";
import { noop } from "lodash";

import ErrorMessage from "./ErrorMessage";
import { useAllLevel0Resources } from "@hooks/query/useAllLevel0Resources";
import { Level0WRSSource } from "@services/level0Resource";

type SelectProps = React.ComponentProps<Select>;

type SelectLevel0ResourceProps = Exclude<SelectProps, "value" | "onChange"> & {
  value?: string;
  onChange?: (e: any) => void;
  level1ResourceId?: string;
  source?: Level0WRSSource | Level0WRSSource[];
  errorMessage?: string;
  excludeIds?: string[];
  onlySALevel0Resources?: boolean;
  defaultValueId?: string;
};

const SelectLevel0Resource: React.FunctionComponent<
  SelectLevel0ResourceProps
> = ({
  value,
  onChange = noop,
  level1ResourceId,
  source,
  errorMessage,
  excludeIds = [],
  onlySALevel0Resources = false,
  defaultValueId,
  ...restProps
}) => {
  const { isLoading, data: options = [] } = useAllLevel0Resources({
    params: {
      parentId: level1ResourceId,
      source,
    },
    select: (data: any) => {
      const mapped = data
        .filter((i: any) => !excludeIds.includes(i.id))
        .map((i: any) => {
          const hasClasses = i.waterClasses.length > 0;
          return {
            label: i.identifier,
            value: i.id,
            yield: i.yield,
            source: i.source,
            hasClasses,
            saApproval: i.saApproval,
          };
        });

      return onlySALevel0Resources
        ? mapped.filter((member: { saApproval: boolean }) => member.saApproval)
        : mapped;
    },
    refetchOnWindowFocus: true,
  });

  // Preselect the requested default once options are available, only while
  // nothing is selected yet — a user's explicit choice is never overridden.
  useEffect(() => {
    if (isLoading || !defaultValueId || value) return;
    const defaultOption = options.find((i: any) => i.value === defaultValueId);
    if (defaultOption) {
      onChange(defaultOption);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, defaultValueId]);

  if (isLoading) {
    return null;
  }

  return (
    <>
      <Select
        options={options}
        value={
          options.find((i: any) => {
            return i.value === value || i.label === value;
          }) || null
        }
        onChange={onChange}
        openMenuOnFocus
        {...restProps}
      />

      {errorMessage ? <ErrorMessage>{errorMessage}</ErrorMessage> : null}
    </>
  );
};

export default SelectLevel0Resource;
